import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { motion } from "framer-motion";
import "../CSS/HeaderNavMain.css";

const LINKS_HOME = [
  { id: "games", label: "Games", href: "#games" },
  { id: "contact", label: "Contact", href: "#contact" },
  {
    id: "store",
    label: "Play Store",
    href: "https://play.google.com/store/apps/dev?id=6648079857840378896",
    external: true,
  },
];

const LINKS_FLOATBLOCK = [
  { id: "home", label: "Home", href: "/home" },
  {
    id: "store",
    label: "Play Store",
    href: "https://play.google.com/store/apps/dev?id=6648079857840378896",
    external: true,
  },
];

export default function HeaderNavMain() {
  const pillHeight = 35;
  const horizontalPadding = 10;

  const refs = useRef([]);
  const hoverTimeout = useRef(null);
  const stretchTimeout = useRef(null);
  const activeRef = useRef("store");

  const [active, setActive] = useState("store");
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const [pillStyle, setPillStyle] = useState({
    left: 0,
    top: 0,
    width: 0,
  });

  const [pillInitialized, setPillInitialized] = useState(false);
  const [enableTransition, setEnableTransition] = useState(false);

  // "desktop" | "tablet" | "mobile"
  const [device, setDevice] = useState("desktop");

  const [links] = useState(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path.startsWith("/floatblock")) {
        return LINKS_FLOATBLOCK;
      }
    }
    return LINKS_HOME;
  });

  const setRef = (el, i) => {
    refs.current[i] = el;
  };

  const calcForElement = (el) => {
    const rect = el.getBoundingClientRect();
    const parentRect = el.parentElement.getBoundingClientRect();

    const left = rect.left - parentRect.left - horizontalPadding / 2;
    const width = rect.width + horizontalPadding;
    const top = rect.top - parentRect.top + rect.height / 2 - pillHeight / 2;

    return { left, top, width };
  };

  const positionPillAt = (id) => {
    const index = links.findIndex((l) => l.id === id);
    const el = refs.current[index];
    if (!el) return;

    const { left, top, width } = calcForElement(el);
    setPillStyle({ left, top, width });
  };

  const animateTo = (nextId) => {
    if (nextId === activeRef.current) return;

    clearTimeout(hoverTimeout.current);
    clearTimeout(stretchTimeout.current);

    hoverTimeout.current = setTimeout(() => {
      const indexNext = links.findIndex((l) => l.id === nextId);
      const elNext = refs.current[indexNext];
      if (!elNext) return;

      const indexCurrent = links.findIndex((l) => l.id === activeRef.current);
      const elCurrent = refs.current[indexCurrent] || elNext;

      const cur = calcForElement(elCurrent);
      const next = calcForElement(elNext);

      const goingRight = next.left > cur.left;
      const distance = Math.abs(next.left - cur.left);
      const stretchLeft = goingRight ? cur.left : next.left;
      const stretchWidth = distance + Math.max(cur.width, next.width);

      setPillStyle({
        left: stretchLeft,
        top: next.top,
        width: stretchWidth,
      });

      stretchTimeout.current = setTimeout(() => {
        setPillStyle({
          left: next.left,
          top: next.top,
          width: next.width,
        });
        setActive(nextId);
        activeRef.current = nextId;
      }, 170);
    }, 30);
  };

  useLayoutEffect(() => {
    positionPillAt("store");
    setPillInitialized(true);

    return () => {
      clearTimeout(hoverTimeout.current);
      clearTimeout(stretchTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links]);

  useEffect(() => {
    if (pillInitialized) setEnableTransition(true);
  }, [pillInitialized]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const getDeviceFromWidth = (w) => {
      if (w >= 1024) return "desktop";
      if (w >= 768) return "tablet";
      return "mobile";
    };

    const updateLayout = () => {
      const width = window.innerWidth;
      const nextDevice = getDeviceFromWidth(width);

      setDevice(nextDevice);

      if (nextDevice !== "mobile") {
        setEnableTransition(false);
        requestAnimationFrame(() => {
          positionPillAt(activeRef.current || "store");
          requestAnimationFrame(() => setEnableTransition(true));
        });
      }
    };

    updateLayout();

    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links]);


  const handleNavClick = (link) => {
    if (typeof window === "undefined") return;

    const path = window.location.pathname;
    const isHome = path === "/home" || path === "/";

    if (!link.external && link.href.startsWith("#")) {
      const targetUrl = isHome ? link.href : `/home${link.href}`;
      window.location.href = targetUrl;
      return;
    }

    if (!link.external && !link.href.startsWith("#")) {
      window.location.href = link.href;
      return;
    }

    if (link.external) {
      window.open(link.href, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <header className="relative z-30 w-full max-w-5xl mx-auto px-6 py-6">

      {/*  MOBILE (< md) */}
      {device === "mobile" && (
        <div className="relative flex items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <img
              src="/assets/images/logo2.png"
              alt="FloatKeyGames Logo"
              className="w-10 h-10 rounded-2xl object-cover shadow-[0_10px_30px_rgba(0,0,0,0.75)]"
            />
            <div className="flex flex-col items-start text-left">
              <h1 className="text-[15px] font-semibold text-white">
                FloatKeyGames
              </h1>
              <p className="text-[11px] text-slate-400">Indie Game Studio</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsMobileOpen((prev) => !prev)}
            className="relative w-10 h-10 flex items-center justify-center
              rounded-xl border border-white/20 bg-white/5 backdrop-blur-xl
              shadow-[0_12px_28px_rgba(0,0,0,0.9)]
              active:scale-95 transition-transform duration-150"
          >
            <span className="flex flex-col gap-[3px]">
              <span
                className={`block h-[2px] w-[16px] bg-white rounded-full transition-all duration-200 ${
                  isMobileOpen ? "translate-y-[4px] rotate-45" : ""
                }`}
              />
            <span
                className={`block h-[2px] w-[16px] bg-white rounded-full transition-opacity duration-150 ${
                  isMobileOpen ? "opacity-0" : "opacity-80"
                }`}
              />
              <span
                className={`block h-[2px] w-[16px] bg-white rounded-full transition-all duration-200 ${
                  isMobileOpen ? "-translate-y-[4px] -rotate-45" : ""
                }`}
              />
            </span>
          </button>

          {isMobileOpen && (
            <motion.nav
              initial={{ opacity: 0, y: -8, x: 10 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, y: -8, x: 10 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="
                absolute top-[60px] right-0
                w-[200px]
                rounded-2xl
                bg-white/5 backdrop-blur-xl
                border border-white/20
                shadow-[0_16px_40px_rgba(0,0,0,0.85)]
              "
            >
              <ul className="flex flex-col py-2">
                {links.map((link, index) => (
                  <li key={link.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsMobileOpen(false);
                        handleNavClick(link);
                      }}
                      className="
                        group flex items-center justify-between
                        w-full px-4 py-2.5 text-[13px]
                        transition-all duration-200
                        text-slate-200 hover:bg-white/[0.06] hover:text-white
                      "
                    >
                      <span className="font-medium tracking-tight group-hover:brightness-110">
                        {link.label}
                      </span>

                      <span
                        className="
                          h-[7px] w-[7px] rounded-full transition-all
                          bg-slate-500/75 group-hover:bg-indigo-500 
                          group-hover:shadow-[0_0_6px_rgba(255,255,255,0.8)]
                        "
                      />
                    </button>

                    {index < links.length - 1 && (
                      <div className="mx-4 h-px bg-white/5" />
                    )}
                  </li>
                ))}
              </ul>
            </motion.nav>
          )}
        </div>
      )}

      {/* TABLET (md–lg) */}
      {device === "tablet" && (
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <img
              src="/assets/images/logo2.png"
              alt="FloatKeyGames Logo"
              className="w-12 h-12 rounded-2xl object-cover shadow-xl"
            />
            <div className="flex flex-col items-start text-left">
              <h1 className="text-lg font-semibold text-white drop-shadow-md">
                FloatKeyGames
              </h1>
              <p className="text-xs text-slate-300">Indie Game Studio</p>
            </div>
          </div>

          <nav className="flex items-center relative text-white gap-8">
            <div className="relative flex items-center gap-1">
              {pillInitialized && (
                <div
                  className={`absolute rounded-full bg-[rgba(255,255,255,0.08)]
                    backdrop-blur-[14px] border border-[rgba(255,255,255,0.12)]
                    overflow-hidden pointer-events-none
                    ${
                      enableTransition
                        ? "transition-all duration-200 ease-out"
                        : ""
                    }`}
                  style={{
                    height: `${pillHeight}px`,
                    left: pillStyle.left,
                    top: pillStyle.top,
                    width: pillStyle.width,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[liquid_6s_linear_infinite]" />
                </div>
              )}

              {links.map((link, i) => (
                <button
                  key={link.id}
                  ref={(el) => setRef(el, i)}
                  onMouseEnter={() => animateTo(link.id)}
                  onClick={() => handleNavClick(link)}
                  className={`relative z-10 px-3 flex items-center justify-center 
                    font-medium text-[15px] leading-none tracking-wide transition-all duration-150 ${
                      active === link.id
                        ? "text-white"
                        : "text-slate-300 hover:text-indigo-100"
                    }`}
                  style={{ height: `${pillHeight}px` }}
                >
                  <span className="inline-block w-full text-center">
                    {link.label}
                  </span>
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}

      {/* PC (lg+) */}
      {device === "desktop" && (
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <img
              src="/assets/images/logo2.png"
              alt="FloatKeyGames Logo"
              className="w-12 h-12 rounded-2xl object-cover shadow-xl"
            />
            <div className="flex flex-col items-start text-left">
              <h1 className="text-lg font-semibold text-white drop-shadow-md">
                FloatKeyGames
              </h1>
              <p className="text-xs text-slate-300">Indie Game Studio</p>
            </div>
          </div>

          <nav className="flex items-center relative text-white gap-8">
            <div className="relative flex items-center gap-1">
              {pillInitialized && (
                <div
                  className={`absolute rounded-full bg-[rgba(255,255,255,0.08)]
                    backdrop-blur-[14px] border border-[rgba(255,255,255,0.12)]
                    overflow-hidden pointer-events-none
                    ${
                      enableTransition
                        ? "transition-all duration-200 ease-out"
                        : ""
                    }`}
                  style={{
                    height: `${pillHeight}px`,
                    left: pillStyle.left,
                    top: pillStyle.top,
                    width: pillStyle.width,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[liquid_6s_linear_infinite]" />
                </div>
              )}

              {links.map((link, i) => (
                <button
                  key={link.id}
                  ref={(el) => setRef(el, i)}
                  onMouseEnter={() => animateTo(link.id)}
                  onClick={() => handleNavClick(link)}
                  className={`relative z-10 px-3 flex items-center justify-center 
                    font-medium text-[15px] leading-none tracking-wide transition-all duration-150 ${
                      active === link.id
                        ? "text-white"
                        : "text-slate-300 hover:text-indigo-100"
                    }`}
                  style={{ height: `${pillHeight}px` }}
                >
                  <span className="inline-block w-full text-center">
                    {link.label}
                  </span>
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
      
    </header>
  );
}
